import { useQuery } from "@apollo/client/react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PageWrapper from "components/PageWrapper";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystatePrColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";

import StackDetachedIntegrationCallout from "../../components/DetachedIntegrationCallout";
import StackHeader from "../../components/Header";
import { StackContext } from "../../Context";
import { getStacksBackUrl } from "../../helpers";
import PullRequestElement from "./components/PullRequestElement";
import { LIST_PULL_REQUESTS } from "./gql";

const StackPullRequestsListLegacy = () => {
  const { stack: stackCtx } = useTypedContext(StackContext);

  const providerBasedName =
    stackCtx.provider === VcsProvider.Gitlab ? "Merge Request" : "Pull Request";

  useTitle(`${providerBasedName}s · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { loading, error, data, refetch } = useQuery(LIST_PULL_REQUESTS, {
    variables: { id: stackCtx.id },
    skip: stackCtx.vcsDetached,
  });

  const pullRequests = data?.stack?.pullRequests;

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // Do not show 'loading' when polling in the background.
  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  // beware the `vcs Detached` is querying only for the stack context
  if (!data?.stack && !stackCtx.vcsDetached) {
    return <NotFoundPage />;
  }

  const pageTitle = stackCtx.provider === VcsProvider.Gitlab ? "Merge requests" : "Pull requests";
  const emptyStateTitle =
    stackCtx.provider === VcsProvider.Gitlab ? "No merge requests yet" : "No pull requests yet";

  return (
    <>
      <StackHeader />

      <PageInfoHeader title={pageTitle}>
        <Button
          onPress={() => refetch()}
          disabled={loading}
          loading={!!(loading && data?.stack)}
          variant="primary"
        >
          Refresh
        </Button>
      </PageInfoHeader>

      <StackDetachedIntegrationCallout />

      <PageWrapper>
        {!stackCtx.vcsDetached &&
          pullRequests?.map((pr) => (
            <PullRequestElement key={pr.id} repository={stackCtx.repository} pr={pr} />
          ))}

        {stackCtx.vcsDetached && (
          <EmptyState
            title="Source code integration was detached from this stack."
            icon={EmptystatePrColored}
          />
        )}

        {!stackCtx.vcsDetached && pullRequests?.length === 0 && (
          <EmptyState
            title={emptyStateTitle}
            icon={EmptystatePrColored}
            caption={
              <>
                This is a list of {providerBasedName}s currently open against this stack's tracked
                branch on {HUMANIZE_PROVIDER[stackCtx.provider]}. If something seems missing, it may
                be because the {providerBasedName} was opened before the creation of this stack, or
                it may be targeting a different branch.
              </>
            }
          />
        )}
      </PageWrapper>
    </>
  );
};

export default StackPullRequestsListLegacy;
